import EmptyFolder from 'assets/img/app/empty-folder.png';
import { ImageVisualizer, SidebarMenu, SolidButton } from "components";
import { useEffect, useRef, useState } from "react";
import { formatDateToISODateString, formatDateToLocaleDateString, formatISODateStringToDate } from "utils";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControlLabel,
    Pagination,
    CircularProgress,
    TextField,
    Typography,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Stack,
    Avatar,
    Chip,
    Button,
    List,
    ListItem
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { useAppSelector } from 'hooks';


export const OrdersPage = () => {
    const { token } = useAppSelector(state => state.user);
    const today = new Date();
    const recordsPerView = 10;
    const MAX_RANGE_MONTHS = 6;
    const filtersPerPage = 8;
    const [displaySearch, setDisplaySearch] = useState<string>('');
    const [displayPage, setDisplayPage] = useState<number>(1);
    const [sucursalSearch, setSucursalSearch] = useState<string>('');
    const [sucursalPage, setSucursalPage] = useState<number>(1);

    // Calcular la fecha mínima del sistema (6 meses atrás)
    const systemMin = (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 6);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    })();

    // Inicializar el rango por defecto (último mes)
    const defaultStart = (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    })();

    // Función auxiliar para agregar meses a una fecha
    const addMonths = (date: Date, months: number) => {
        const d = new Date(date.getTime());
        const day = d.getDate();
        d.setMonth(d.getMonth() + months);
        if (d.getDate() < day) {
            d.setDate(0);
        }
        return d;
    };

    // Función para normalizar el rango de fechas respetando el límite de 6 meses
    const normalizeWithAnchor = (start: Date, end: Date, anchor: 'start' | 'end') => {
        let s = start;
        let e = end;

        if (anchor === 'start') {
            const maxEnd = addMonths(s, MAX_RANGE_MONTHS);
            const cappedMaxEnd = maxEnd > today ? today : maxEnd;
            if (e < s) e = cappedMaxEnd;
            if (e > cappedMaxEnd) e = cappedMaxEnd;
        } else {
            // ensure start is valid (>= systemMin) and end - start <= MAX_RANGE_MONTHS
            const minStart = addMonths(e, -MAX_RANGE_MONTHS);
            const cappedMinStart = minStart < systemMin ? systemMin : minStart;

            // Si start es mayor que end, ajustarlo
            if (s > e) {
                s = cappedMinStart;
            } else if (s < cappedMinStart) {
                // Solo ajustar si el rango excede los 6 meses
                s = cappedMinStart;
            }
            // Si start >= systemMin y está dentro del rango de 6 meses, mantenerlo
        }

        return [s, e] as const;
    };

    // estado para almacenar los benchmarks recuperados
    const [loading, setLoading] = useState<boolean>(false);

    // estados para almacenar los datos del reporte
    const [reports, setReports] = useState<any[][]>([]);
    const [backupReports, setBackupReports] = useState<any[]>([]);

    // estados para manejar la paginación
    const [paginationCount, setPaginationCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    // estados para manejar las pantallas de carga

    // estado que almacena el inicio del rango de fechas de los benchmarks consultadas
    const [startDate, setStartDate] = useState<Date>(defaultStart);
    const [finishDate, setFinishDate] = useState<Date>(new Date());

    // estado para almacenar el arreglo de imágenes que se mostrarán en pantalla
    const [imagesToShow, setImagesToShow] = useState<string[]>([]);
    const [showImageModal, setShowImageModal] = useState<boolean>(false);

    // estados para almacenar los valores que aparecerán en los filtros de búsqueda
    const [searchFilters, setSearchFilters] = useState<{ user: any[], sucursal: any[] }>({ user: [], sucursal: [] });

    const contentRef = useRef<HTMLDivElement>(null);

    const getBenchmarkDocs = async (signal: AbortSignal) => {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/benchmark/get/report`, {
            params: {
                startDate: `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`,
                endDate: `${finishDate.getFullYear()}-${(finishDate.getMonth() + 1).toString().padStart(2, '0')}-${finishDate.getDate().toString().padStart(2, '0')}`,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `UserToken ${token}`
            },
            signal: signal
        })

        const benchmarksData = response.data?.data?.orders || [];

        setPaginationCount(Math.ceil(benchmarksData.length / recordsPerView));
        setCurrentPage(1);
        setReports(benchmarksData)
        setBackupReports(benchmarksData)

        setSearchFilters(() => {
            let user = response.data?.data?.accounts || [];
            let sucursal = response.data?.data?.sucursals || [];
            user = user.map((account: any) => ({ ...account, checked: true }))
            sucursal = sucursal.map((suc: any) => ({ ...suc, checked: true }))
            return {
                sucursal: [{
                    key: 'all',
                    name: 'Todas las sucursales',
                    checked: true
                }, ...sucursal],
                user: [{
                    key: 'all',
                    name: 'Todos los displays',
                    lastName: '',
                    checked: true
                }, ...user]
            }
        })
        setLoading(false);
    }

    useEffect(() => {
        const controller = new AbortController();
        getBenchmarkDocs(controller.signal);
        return () => {
            controller.abort();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePaginationHandler = async (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    const showImageHandler = (images: string[]) => {
        setImagesToShow(images);
        setShowImageModal(true);
    }

    const changeSearchFilterHandler = (item: any, filterType: "user" | "sucursal") => {

        setSearchFilters((prevFilters) => {
            let updatedFilterItems = prevFilters[filterType].map((filterItem: any) => {
                if (filterItem.key === item.key) {
                    return { ...filterItem, checked: !filterItem.checked };
                }
                return filterItem;
            });

            if (item.key === 'all') {
                updatedFilterItems = updatedFilterItems.map((filterItem: any) => ({ ...filterItem, checked: !item.checked }));
                setReports(() => item.checked ? [] : backupReports);
            } else {
                setReports(() => {
                    const filteredReports = backupReports.filter((r: any) => {
                        const filterItem = updatedFilterItems.find((fi: any) => fi.key === r[filterType === 'user' ? 'displayKey' : 'sucursalId']);
                        return filterItem ? filterItem.checked : false;
                    });
                    return filteredReports;
                })
            }
            return {
                ...prevFilters,
                [filterType]: updatedFilterItems
            };
        })
    }

    return (
        <>
            <Box>
                <Box ref={contentRef} sx={{ width: '100%', height: '100vh', overflow: 'auto' }}>
                    <Box sx={{ width: '100%', height: '100vh', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                        {/* Menú lateral */}
                        <Box sx={{ gridColumn: 'span 2', borderRight: 2, borderColor: '#EBEBEB' }}>
                            <SidebarMenu pageName="orders" />
                        </Box>

                        <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', flexGrow: 1, gridColumn: 'span 10' }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                // background: 'linear-gradient(135deg, #183555 0%, #2c5f8d 100%)',
                                px: 4,
                                py: 3,
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <ShoppingCartIcon sx={{ fontSize: 28, color: '#183555' }} />
                                    <Typography variant="h5" sx={{ color: '#183555' }} fontWeight="bold">Reporte de Pedidos</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.15)', px: 2, py: 1, borderRadius: 2 }}>
                                    <CalendarTodayIcon sx={{ color: '#183555', fontSize: 20 }} />
                                    <Typography variant="body1" fontWeight="600" sx={{ color: '#183555' }}>Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}</Typography>
                                </Stack>
                            </Box>

                            <Box
                                id="main-section"
                                sx={{ width: '100%', height: '100vh', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}
                            >
                                {loading ? (
                                    <Box sx={{ gridColumn: 'span 12', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                        <CircularProgress color="primary" size={50} />
                                    </Box>
                                ) : (
                                    <>
                                        {reports.length === 0 ?
                                            <Box sx={{ gridColumn: 'span 9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2, p: 4 }}>
                                                <img src={EmptyFolder} alt="empty" style={{ height: '200px', objectFit: 'contain' }} />
                                                <Typography variant="h6" textAlign="center" fontWeight="600" color="primary">
                                                    No se encontraron resultados para tu búsqueda
                                                </Typography>
                                            </Box>
                                            :
                                            <Box sx={{ gridColumn: 'span 9', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                                                <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                    <TableContainer>
                                                        <Table>
                                                            <TableHead>
                                                                <TableRow sx={{
                                                                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e3e8ef 100%)',
                                                                    borderBottom: '2px solid #183555'
                                                                }}>
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <PersonIcon fontSize="small" color="action" />
                                                                            <Typography variant="subtitle2" fontWeight="600">Display</Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <CalendarTodayIcon fontSize="small" color="action" />
                                                                            <Typography variant="subtitle2" fontWeight="600">Fecha</Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <StoreIcon fontSize="small" color="action" />
                                                                            <Typography variant="subtitle2" fontWeight="600">Sucursal</Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                                                            <ImageIcon fontSize="small" color="action" />
                                                                            <Typography variant="subtitle2" fontWeight="600">Imagen</Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {
                                                                    reports.map((item: any, i: number) => {
                                                                        const avatarColors = ['#183555', '#2e7d32', '#d32f2f', '#f57c00', '#7b1fa2', '#0288d1'];
                                                                        const avatarBg = avatarColors[i % avatarColors.length];
                                                                        return (
                                                                            i >= (recordsPerView * currentPage - recordsPerView) && i < (recordsPerView * currentPage) ?
                                                                                <TableRow
                                                                                    key={i}
                                                                                    hover
                                                                                    sx={{
                                                                                        '&:hover': { bgcolor: '#f8f9fa' },
                                                                                        transition: 'all 0.2s ease'
                                                                                    }}
                                                                                >
                                                                                    <TableCell>
                                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                                            <Avatar sx={{
                                                                                                width: 36,
                                                                                                height: 36,
                                                                                                bgcolor: avatarBg,
                                                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                                                                            }}>
                                                                                                <PersonIcon sx={{ fontSize: 18 }} />
                                                                                            </Avatar>
                                                                                            <Typography variant="body2" fontWeight="500">{item.name}</Typography>
                                                                                        </Stack>
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Chip
                                                                                            icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
                                                                                            label={item.created}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                bgcolor: '#e3f2fd',
                                                                                                color: '#1565c0',
                                                                                                fontWeight: 600,
                                                                                                border: '1px solid #90caf9',
                                                                                                '& .MuiChip-icon': { color: '#1565c0' }
                                                                                            }}
                                                                                        />
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                                            <StoreIcon fontSize="small" color="action" />
                                                                                            <Typography variant="body2">{item.nameSucursal}</Typography>
                                                                                        </Stack>
                                                                                    </TableCell>
                                                                                    <TableCell align="center">
                                                                                        {
                                                                                            item.image !== null &&
                                                                                            <Button
                                                                                                variant="contained"
                                                                                                size="small"
                                                                                                startIcon={<ImageIcon />}
                                                                                                onClick={() => showImageHandler([item.image])}
                                                                                                sx={{
                                                                                                    textTransform: 'none',
                                                                                                    // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                                                    // boxShadow: '0 2px 4px rgba(102, 126, 234, 0.4)',
                                                                                                    '&:hover': {
                                                                                                        // background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                                                                                        // boxShadow: '0 4px 8px rgba(102, 126, 234, 0.6)'
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                Ver imagen
                                                                                            </Button>
                                                                                        }
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                                :
                                                                                null
                                                                        )
                                                                    })
                                                                }
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Card>

                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto', py: 3 }}>
                                                    <Pagination
                                                        size="large"
                                                        color="primary"
                                                        count={paginationCount}
                                                        defaultPage={1}
                                                        page={currentPage}
                                                        siblingCount={1}
                                                        boundaryCount={2}
                                                        onChange={changePaginationHandler}
                                                    />
                                                </Box>
                                            </Box>
                                        }
                                    </>
                                )}

                                {/* Filtros de búsqueda */}
                                {
                                    !loading &&
                                    <Box sx={{ gridColumn: 'span 3', overflowY: 'auto', display: 'flex', p: 2, flexDirection: 'column', gap: 1.5, borderLeft: 2, borderColor: '#EBEBEB', bgcolor: '#fafbfc' }}>
                                        <Accordion sx={{
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:before': { display: 'none' },
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                                aria-controls="panel-fecha-content"
                                                id="panel-fecha-header"
                                                sx={{ bgcolor: '#f5f7fa' }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CalendarTodayIcon fontSize="small" sx={{ color: '#1565c0' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#183555">Fecha</Typography>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={3} alignItems="center">
                                                    <Box sx={{ width: '100%' }}>
                                                        <Typography variant="subtitle2" fontWeight="600" gutterBottom>Fecha de inicio</Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="date"
                                                            value={formatDateToISODateString(startDate)}
                                                            inputProps={{
                                                                min: formatDateToISODateString(systemMin),
                                                                max: formatDateToISODateString(finishDate)
                                                            }}
                                                            onChange={(e: any) => {
                                                                const selectedDate = e.target.value ? formatISODateStringToDate(e.target.value, false) : new Date();
                                                                if (!selectedDate) return;
                                                                const [s, f] = normalizeWithAnchor(selectedDate, finishDate, 'start');
                                                                setStartDate(s ?? new Date());
                                                                setFinishDate(f ?? new Date());
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ width: '100%' }}>
                                                        <Typography variant="subtitle2" fontWeight="600" gutterBottom>Fecha de fin</Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="date"
                                                            value={formatDateToISODateString(finishDate)}
                                                            inputProps={{
                                                                min: formatDateToISODateString(systemMin),
                                                                max: formatDateToISODateString(addMonths(startDate, MAX_RANGE_MONTHS) > today ? today : addMonths(startDate, MAX_RANGE_MONTHS))
                                                            }}
                                                            onChange={(e: any) => {
                                                                const selectedDate = e.target.value ? formatISODateStringToDate(e.target.value, false) : new Date();
                                                                if (!selectedDate) return;
                                                                const [s, f] = normalizeWithAnchor(startDate, selectedDate, 'end');
                                                                setStartDate(s ?? new Date());
                                                                setFinishDate(f ?? new Date());
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ width: '100%' }}>
                                                        <SolidButton text="Aplicar" backgroundColor="primary" disabled={false} onClickHandler={() => {
                                                            getBenchmarkDocs(new AbortController().signal);
                                                        }} />
                                                    </Box>
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion sx={{
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:before': { display: 'none' },
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                                aria-controls="panel-displays-content"
                                                id="panel-displays-header"
                                                sx={{ bgcolor: '#f5f7fa' }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PersonIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#183555">Displays</Typography>
                                                    <Chip
                                                        label={searchFilters.user.length}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#e8f5e9',
                                                            color: '#2e7d32',
                                                            fontWeight: 600,
                                                            border: '1px solid #a5d6a7'
                                                        }}
                                                    />
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {searchFilters.user.length === 0 ? (
                                                    <Typography variant="body2" fontWeight="600" color="primary">No hay datos disponibles</Typography>
                                                ) : (
                                                    <Box>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Buscar display..."
                                                            value={displaySearch}
                                                            onChange={(e) => {
                                                                setDisplaySearch(e.target.value);
                                                                setDisplayPage(1);
                                                            }}
                                                            InputProps={{
                                                                startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                            }}
                                                            sx={{ mb: 2 }}
                                                        />
                                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                            {searchFilters.user
                                                                .filter((item: any) =>
                                                                    (item.name + " " + item.lastName).toLowerCase().includes(displaySearch.toLowerCase())
                                                                )
                                                                .slice((displayPage - 1) * filtersPerPage, displayPage * filtersPerPage)
                                                                .map((item: any) => (
                                                                    <ListItem key={item.key} dense disablePadding>
                                                                        <FormControlLabel
                                                                            sx={{ width: '100%' }}
                                                                            label={<Typography variant="body2">{item.name + " " + item.lastName}</Typography>}
                                                                            control={
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    id={item.key}
                                                                                    name={item.name + " " + item.lastName}
                                                                                    value={item.key}
                                                                                    checked={item.checked}
                                                                                    onChange={(event) => changeSearchFilterHandler(item, "user")}
                                                                                />
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            }
                                                        </List>
                                                        {Math.ceil(searchFilters.user.filter((item: any) =>
                                                            (item.name + " " + item.lastName).toLowerCase().includes(displaySearch.toLowerCase())
                                                        ).length / filtersPerPage) > 1 && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                                    <Pagination
                                                                        size="small"
                                                                        count={Math.ceil(searchFilters.user.filter((item: any) =>
                                                                            (item.name + " " + item.lastName).toLowerCase().includes(displaySearch.toLowerCase())
                                                                        ).length / filtersPerPage)}
                                                                        page={displayPage}
                                                                        onChange={(e, page) => setDisplayPage(page)}
                                                                    />
                                                                </Box>
                                                            )}
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion sx={{
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:before': { display: 'none' },
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                                aria-controls="panel-sucursales-content"
                                                id="panel-sucursales-header"
                                                sx={{ bgcolor: '#f5f7fa' }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <StoreIcon fontSize="small" sx={{ color: '#f57c00' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#183555">Sucursales</Typography>
                                                    <Chip
                                                        label={searchFilters.sucursal.length}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#fff3e0',
                                                            color: '#f57c00',
                                                            fontWeight: 600,
                                                            border: '1px solid #ffcc80'
                                                        }}
                                                    />
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {searchFilters.sucursal.length === 0 ? (
                                                    <Typography variant="body2" fontWeight="600" color="primary">No hay datos disponibles</Typography>
                                                ) : (
                                                    <Box>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Buscar sucursal..."
                                                            value={sucursalSearch}
                                                            onChange={(e) => {
                                                                setSucursalSearch(e.target.value);
                                                                setSucursalPage(1);
                                                            }}
                                                            InputProps={{
                                                                startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                            }}
                                                            sx={{ mb: 2 }}
                                                        />
                                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                            {searchFilters.sucursal
                                                                .filter((item: any) =>
                                                                    item.name.toLowerCase().includes(sucursalSearch.toLowerCase())
                                                                )
                                                                .slice((sucursalPage - 1) * filtersPerPage, sucursalPage * filtersPerPage)
                                                                .map((item: any) => (
                                                                    <ListItem key={item.key} dense disablePadding>
                                                                        <FormControlLabel
                                                                            sx={{ width: '100%' }}
                                                                            label={<Typography variant="body2">{item.name}</Typography>}
                                                                            control={
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    id={item.key}
                                                                                    name={item.name}
                                                                                    value={item.key}
                                                                                    checked={item.checked}
                                                                                    onChange={(event) => changeSearchFilterHandler(item, "sucursal")}
                                                                                />
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            }
                                                        </List>
                                                        {Math.ceil(searchFilters.sucursal.filter((item: any) =>
                                                            item.name.toLowerCase().includes(sucursalSearch.toLowerCase())
                                                        ).length / filtersPerPage) > 1 && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                                    <Pagination
                                                                        size="small"
                                                                        count={Math.ceil(searchFilters.sucursal.filter((item: any) =>
                                                                            item.name.toLowerCase().includes(sucursalSearch.toLowerCase())
                                                                        ).length / filtersPerPage)}
                                                                        page={sucursalPage}
                                                                        onChange={(e, page) => setSucursalPage(page)}
                                                                    />
                                                                </Box>
                                                            )}
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box>
                                }
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
            {
                showImageModal &&
                <>
                    <ImageVisualizer imageArray={imagesToShow} imageAlt="prueba" clickOutsideHandler={() => setShowImageModal(false)} />
                </>
            }
        </>
    )
}